import type { DeviceState, NextOpening } from "@vaulted/shared";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { get } from "../api";
import { Frame } from "./Forms";
import { LobbySignage } from "./LobbySignage";

/**
 * The lobby display: a clinic device (an Android screen in Fully Kiosk, portrait) whose welcome has
 * Check in, Walk in, and Book; a screen saver after a minute on the welcome; and every other screen
 * wiped after a minute untouched ("Still there?", then the whole page reloads), so nobody's details
 * are left on it.
 */

const LOBBY_KEY = "vaulted-device-lobby";
/** Remembered so the page skips "Tap to start" (the kiosk app keeps the screen on) after a reload. */
export const isLobby = () => {
  try {
    return localStorage.getItem(LOBBY_KEY) === "1";
  } catch {
    return false;
  }
};
export const rememberLobby = (on: boolean) => {
  try {
    if (on) localStorage.setItem(LOBBY_KEY, "1");
    else localStorage.removeItem(LOBBY_KEY);
  } catch {
    /* nothing to remember */
  }
};

/** Untouched this long (anywhere but the welcome): "Still there?". */
const IDLE_MS = 60_000;
/** Then this long to tap before it's wiped. */
const WARN_MS = 15_000;
/** On the welcome, untouched this long: the screen saver. */
const SAVER_MS = 60_000;
/** A finished booking's confirmation stays this long. */
const DONE_MS = 20_000;

/**
 * Wipe the screen: sign out of online booking (its sign-in cookie), forget anything the booking page
 * kept in the browser (all but this device's own key), and reload the whole page, so nothing typed
 * stays in memory.
 */
export async function wipe(slug: string, finish?: () => Promise<unknown>) {
  await Promise.allSettled([
    finish?.(),
    fetch(`/api/book/${slug}/signout`, { method: "POST", headers: { "x-vaulted-csrf": "1" } }),
  ]);
  try {
    for (const k of Object.keys(localStorage)) if (k.startsWith("vaulted.book")) localStorage.removeItem(k);
    sessionStorage.clear();
  } catch {
    /* nothing kept */
  }
  window.location.replace(window.location.pathname);
}

/** Taps, typing, and scrolling on the page (and inside the booking page's frame) count as someone there. */
function useActivity(onActive: () => void) {
  const cb = useRef(onActive);
  cb.current = onActive;
  useEffect(() => {
    const on = () => cb.current();
    const evts = ["pointerdown", "keydown", "touchstart", "wheel", "lobby-activity"];
    for (const e of evts) window.addEventListener(e, on, { passive: true });
    return () => {
      for (const e of evts) window.removeEventListener(e, on);
    };
  }, []);
}

/** Any screen but the welcome: a minute untouched asks "Still there?", then wipes. */
export function LobbyGuard({ slug, finish }: { slug: string; finish: () => Promise<unknown> }) {
  const [last, setLast] = useState(() => Date.now());
  const [now, setNow] = useState(() => Date.now());
  useActivity(() => setLast(Date.now()));
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const idle = now - last;
  useEffect(() => {
    if (idle >= IDLE_MS + WARN_MS) void wipe(slug, finish);
  }, [idle]); // eslint-disable-line react-hooks/exhaustive-deps
  if (idle < IDLE_MS) return null;
  const left = Math.max(0, Math.ceil((IDLE_MS + WARN_MS - idle) / 1000));
  return (
    <button type="button" onClick={() => setLast(Date.now())} className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-3 bg-ink/95 text-white">
      <span className="font-display text-4xl">Still there?</span>
      <span className="text-lg opacity-90">Tap to keep going</span>
      <span className="text-sm opacity-70">Starting over in {left}…</span>
    </button>
  );
}

/** The next opening, refreshed every minute (the same time online booking would offer). */
function useNextOpening(slug: string) {
  return useQuery({ queryKey: ["next-opening", slug], queryFn: () => get<NextOpening>(`/api/book/${slug}/next-opening`), refetchInterval: 60_000, retry: false });
}

function Banner({ slug, big }: { slug: string; big?: boolean }) {
  const next = useNextOpening(slug).data;
  if (!next?.label) return null;
  return (
    <div className={`rounded-full px-6 py-3 text-center font-semibold shadow-lg ${next.now ? "bg-ok text-white" : "bg-white/95 text-ink"} ${big ? "text-2xl" : "text-lg"}`}>
      {next.now && <span className="mr-2 inline-block size-3 animate-pulse rounded-full bg-white align-middle" aria-hidden />}
      {next.label}
    </div>
  );
}

/** Whether the next opening is later today (on this device's clock: it's in the clinic). */
const openToday = (next: NextOpening | undefined) => !!next && (next.now || (!!next.at && new Date(next.at).toDateString() === new Date().toDateString()));

/** The lobby's welcome: Check in, Walk in (only while something's still open today), Book; the screen saver after a minute untouched. */
export function LobbyWelcome({ slug, state, onCheckIn, onWalkIn, onBook, footer }: { slug: string; state: DeviceState; onCheckIn: () => void; onWalkIn: () => void; onBook: (path?: string) => void; footer: ReactNode }) {
  const specials = state.lobbyMedia?.specials ?? [];
  const walkInToday = openToday(useNextOpening(slug).data);
  const [saver, setSaver] = useState(false);
  const last = useRef(Date.now());
  useActivity(() => (last.current = Date.now()));
  useEffect(() => {
    const t = setInterval(() => {
      if (Date.now() - last.current >= SAVER_MS) setSaver(true);
    }, 1000);
    return () => clearInterval(t);
  }, []);
  // A tap on the screen saver wakes the welcome, or opens what was showing (a featured service's booking, a membership's sign-up).
  if (saver) return <ScreenSaver slug={slug} clinicName={state.clinicName} media={state.lobbyMedia} onWake={(path) => ((last.current = Date.now()), setSaver(false), path && onBook(path))} />;
  const big = "w-full rounded-[10px] px-8 py-7 text-2xl font-semibold shadow-sm";
  return (
    <Frame clinic={state.clinicName}>
      <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col justify-center gap-6 py-10 text-center">
        <h1 className="font-display text-5xl">Welcome to {state.clinicName}</h1>
        <div className="flex justify-center">
          <Banner slug={slug} />
        </div>
        <div className="mt-4 flex flex-col gap-4">
          <button type="button" onClick={onCheckIn} className={`${big} bg-brand text-white`}>
            Check in
            <span className="block text-base font-normal opacity-90">I have an appointment</span>
          </button>
          {state.walkIns && walkInToday && (
            <button type="button" onClick={onWalkIn} className={`${big} border-2 border-brand bg-white text-ink`}>
              Walk in
              <span className="block text-base font-normal text-ink-soft">No appointment: the next open time today</span>
            </button>
          )}
          <button type="button" onClick={() => onBook()} className={`${big} border-2 border-line bg-white text-ink`}>
            Book an appointment
            <span className="block text-base font-normal text-ink-soft">Pick a day and time</span>
          </button>
        </div>
        {specials.map((x) => (
          <SpecialCard key={x.id} special={x} onOpen={x.path ? () => onBook(x.path!) : undefined} />
        ))}
      </div>
      {footer}
    </Frame>
  );
}

type Special = NonNullable<DeviceState["lobbyMedia"]>["specials"][number];

/** A special: its ribbon, headline, and line; tapping it opens its plan or service (when it has one). */
function SpecialCard({ special, onOpen, dark }: { special: Special; onOpen?: () => void; dark?: boolean }) {
  const body = (
    <>
      {special.ribbon && <span className="mb-1 inline-block rounded-full bg-brand px-3 py-0.5 text-sm font-semibold text-white">{special.ribbon}</span>}
      <span className="block font-display text-2xl">{special.headline}</span>
      {special.text && <span className={`mt-1 block text-base ${dark ? "text-white/85" : "text-ink-soft"}`}>{special.text}</span>}
      {onOpen && <span className={`mt-2 block text-sm font-semibold ${dark ? "text-white" : "text-brand"}`}>Tap to learn more</span>}
    </>
  );
  const cls = `w-full rounded-[10px] px-6 py-5 text-center ${dark ? "bg-black/45 text-white backdrop-blur" : "border border-line bg-white text-ink shadow-sm"}`;
  return onOpen ? (
    <button type="button" onClick={onOpen} className={cls}>
      {body}
    </button>
  ) : (
    <div className={cls}>{body}</div>
  );
}

/**
 * The screen saver: the clinic's video (muted, looping) or its name, with the next opening and the
 * specials taking turns; any tap wakes it to the welcome.
 */
function ScreenSaver({ slug, clinicName, media, onWake }: { slug: string; clinicName: string; media: DeviceState["lobbyMedia"]; onWake: (path?: string) => void }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((s) => s + 1), 15_000);
    return () => clearInterval(t);
  }, []);
  const specials = media?.specials ?? [];
  const special = specials.length ? specials[tick % specials.length] : null;
  // The animated menu, when it's turned on (Settings → Devices → Lobby display).
  if (media?.signage) return <LobbySignage slug={slug} clinicName={clinicName} media={media} banner={<Banner slug={slug} big />} onWake={onWake} />;
  const video = media?.videoUrl;
  const top = ["22%", "45%", "65%"][tick % 3];
  return (
    <button type="button" onClick={() => onWake()} className="fixed inset-0 z-50 overflow-hidden bg-gradient-to-b from-ink to-[#3a1030] text-white" aria-label="Tap to start">
      {video ? (
        <>
          <video src={video} autoPlay muted loop playsInline className="absolute inset-0 size-full object-cover" />
          <div className="absolute inset-x-0 top-[8%] flex justify-center px-6">
            <Banner slug={slug} big />
          </div>
          <div className="absolute inset-x-0 bottom-[6%] flex flex-col items-center gap-4 px-8">
            {special && <SpecialCard special={special} dark />}
            <span className="rounded-full bg-black/40 px-5 py-2 text-lg">Tap anywhere to check in or book</span>
          </div>
        </>
      ) : (
        <div className="absolute inset-x-0 flex flex-col items-center gap-6 px-8 transition-[top] duration-[3000ms]" style={{ top }}>
          <span className="font-display text-5xl">{clinicName}</span>
          <Banner slug={slug} big />
          {special && <SpecialCard special={special} dark />}
          <span className="text-xl opacity-80">Tap anywhere to check in or book</span>
        </div>
      )}
    </button>
  );
}

/**
 * Online booking on the lobby display, in a frame with "Start over" always on top. The booking page
 * knows it's here (it remembers nothing, offers no "remember this phone"), and tells this page when
 * it's done; the confirmation stays 20 seconds, then everything is wiped.
 */
export function LobbyBook({ path, onStartOver }: { path: string; onStartOver: () => void }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const on = (e: MessageEvent) => {
      if (e.origin === window.location.origin && e.data === "vaulted-lobby-done") setDone(true);
    };
    window.addEventListener("message", on);
    return () => window.removeEventListener("message", on);
  }, []);
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(onStartOver, DONE_MS);
    return () => clearTimeout(t);
  }, [done]); // eslint-disable-line react-hooks/exhaustive-deps
  // Taps inside the booking page keep the screen from being wiped.
  const watch = () => {
    const doc = frame.current?.contentDocument;
    if (!doc) return;
    for (const evt of ["pointerdown", "keydown", "touchstart", "wheel"]) doc.addEventListener(evt, () => window.dispatchEvent(new Event("lobby-activity")), { passive: true });
  };
  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-line bg-ink px-4 py-3 text-white">
        <span className="font-semibold">{done ? "All set! This screen clears in a moment." : "Book an appointment"}</span>
        <button type="button" onClick={onStartOver} className="rounded-[6px] bg-white px-4 py-2 font-semibold text-ink">
          {done ? "Done" : "Start over"}
        </button>
      </div>
      <iframe ref={frame} title="Book an appointment" src={path} onLoad={watch} className="w-full flex-1 border-0" />
    </div>
  );
}
